import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, Clock, Phone, User, Filter, RefreshCw, CheckCircle, XCircle, AlertCircle, Truck, CreditCard } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { useAppContext } from '../../hooks/useAppContext';
import { differenceInHours, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';

// Types aligned with employee list screen for consistency
type PackageStatus = 'NEW' | 'PAYMENT_CONFIRMED' | 'PROCESSING' | 'READY_FOR_DELIVERY' | 'DELIVERED' | 'CANCELLED';
interface PackageRequest {
    id: string;
    title: string;
    description: string;
    customerName: string;
    customerPhone: string;
    priority: 'low' | 'medium' | 'high';
    status: PackageStatus;
    progressPercent: number;
    creationDate: string;
    lastModified: string;
    employeeId: string;
    employeeName?: string;
    branch: string;
    customerLocation?: string;
    meta?: Record<string, any>;
}

const statusConfig: { [key in PackageStatus]: { label: string, color: string, icon: any } } = {
    'NEW': { label: 'جديد', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20', icon: AlertCircle },
    'PAYMENT_CONFIRMED': { label: 'تم تأكيد الدفع', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', icon: CreditCard },
    'PROCESSING': { label: 'قيد المعالجة', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20', icon: RefreshCw },
    'READY_FOR_DELIVERY': { label: 'جاهز للتسليم', color: 'text-purple-400 bg-purple-400/10 border-purple-400/20', icon: Package },
    'DELIVERED': { label: 'تم التسليم', color: 'text-green-400 bg-green-400/10 border-green-400/20', icon: CheckCircle },
    'CANCELLED': { label: 'ملغي', color: 'text-rose-400 bg-rose-400/10 border-rose-400/20', icon: XCircle },
};

const timeAgo = (isoDate: string) => {
    const date = new Date(isoDate);
    const hours = differenceInHours(new Date(), date);
    if (hours < 24) return `${hours} ساعة مضت`;
    const days = differenceInDays(new Date(), date);
    return `${days} يوم مضى`;
};

const ModernAdminPackageManagementScreen: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<PackageStatus | 'ALL'>('ALL');
    const [packages, setPackages] = useState<PackageRequest[]>([]);
    const [offline, setOffline] = useState(false);

    const fetchPackages = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/package-requests`, {
                headers: user ? { 'X-User-Id': user.id, 'X-User-Role': user.role } : {}
            });
            if (!res.ok) throw new Error(`خطأ في استجابة الخادم: ${res.status}`);
            const data = await res.json().catch(() => []);
            setPackages(Array.isArray(data) ? data : []);
            setOffline(false);
        } catch (err) {
            console.error('Failed to load package requests', err);
            setOffline(true);
            setPackages([]);
            toast.error('تعذر الاتصال بالخادم. يرجى المحاولة لاحقاً.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPackages();
    }, [user]);

    const filtered = useMemo(() => {
        if (!Array.isArray(packages)) return [];
        return packages.filter(p => {
            const matchesQuery = [p.title, p.customerName, p.employeeName, p.branch].some(v => String(v || '').toLowerCase().includes(query.toLowerCase()));
            const matchesStatus = statusFilter === 'ALL' ? true : p.status === statusFilter;
            return matchesQuery && matchesStatus;
        });
    }, [packages, query, statusFilter]);

    return (
        <div className="space-y-8 animate-contentFadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text flex items-center gap-3">
                        <Package className="text-cyan-400" size={32} />
                        إدارة البكجات
                    </h1>
                    <p className="text-slate-400 mt-1">متابعة وإدارة طلبات البكجات وحالاتها</p>
                </div>
                <button
                    onClick={fetchPackages}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 transition-all hover:text-white disabled:opacity-50"
                >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    <span>تحديث البيانات</span>
                </button>
            </div>

            {/* Filters */}
            <div className="glass-card p-4 space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                            placeholder="بحث بعنوان الطلب، العميل، أو الموظف..."
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 pr-10 pl-4 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                        />
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 no-scrollbar">
                        {(['ALL', 'NEW', 'PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED', 'CANCELLED'] as const).map(s => {
                            const config = s === 'ALL' ? { label: 'الكل', color: '', icon: Filter } : statusConfig[s];
                            const isSelected = statusFilter === s;
                            return (
                                <button
                                    key={s}
                                    onClick={() => setStatusFilter(s)}
                                    className={`
                                flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all border
                                ${isSelected
                                            ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.15)]'
                                            : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                                        }
                            `}
                                >
                                    {s !== 'ALL' && <config.icon size={14} />}
                                    <span>{config.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="glass-card p-6 h-48 animate-pulse">
                            <div className="h-6 w-2/3 bg-white/5 rounded mb-4"></div>
                            <div className="h-4 w-1/2 bg-white/5 rounded mb-2"></div>
                            <div className="h-4 w-1/3 bg-white/5 rounded"></div>
                        </div>
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
                        <Package size={40} className="text-slate-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-300">لا توجد طلبات</h3>
                    <p className="text-slate-500 mt-2">لم يتم العثور على أي طلبات تطابق معايير البحث.</p>
                    <button
                        onClick={() => { setQuery(''); setStatusFilter('ALL'); }}
                        className="mt-6 text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                        مسح الفلاتر
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((req, idx) => {
                        const StatusIcon = statusConfig[req.status]?.icon || Package;
                        return (
                            <div
                                key={req.id}
                                onClick={() => navigate(`/admin-packages/${req.id}`, { state: { initial: req } })}
                                className="glass-card p-0 cursor-pointer group hover:-translate-y-1 transition-all duration-300"
                                style={{ animationDelay: `${idx * 50}ms` }}
                            >
                                <div className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-3 rounded-xl bg-gradient-to-br from-white/5 to-white/10 border border-white/5 group-hover:border-cyan-500/30 transition-colors">
                                            <Package className="text-cyan-400" size={24} />
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 ${statusConfig[req.status].color}`}>
                                            <StatusIcon size={12} />
                                            {statusConfig[req.status].label}
                                        </span>
                                    </div>

                                    <h3 className="text-lg font-bold text-white mb-2 line-clamp-1 group-hover:text-cyan-400 transition-colors">
                                        {req.title || 'طلب بدون عنوان'}
                                    </h3>

                                    <div className="space-y-3 mt-4">
                                        <div className="flex items-center gap-3 text-sm text-slate-400">
                                            <User size={16} className="text-slate-500" />
                                            <span className="truncate">{req.employeeName || req.employeeId}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-slate-400">
                                            <Phone size={16} className="text-slate-500" />
                                            <span className="truncate">{req.customerName} - {req.customerPhone}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-slate-400">
                                            <Clock size={16} className="text-slate-500" />
                                            <span>{timeAgo(req.creationDate)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-6 py-4 border-t border-white/5 bg-white/5 flex justify-between items-center">
                                    <span className={`text-xs font-medium px-2 py-1 rounded ${['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(req.status)
                                            ? 'bg-emerald-500/10 text-emerald-400'
                                            : 'bg-rose-500/10 text-rose-400'
                                        }`}>
                                        {['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(req.status) ? 'مدفوع' : 'غير مدفوع'}
                                    </span>
                                    <span className="text-xs text-slate-500 font-mono">
                                        ID: {req.id.substring(0, 8)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ModernAdminPackageManagementScreen;
